import { ApiResponse } from '@interfaces/index';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiUrlConfig } from './configs/apiUrlConfig';
import createApiClient from './configs/httpHandler';

export const userActionsApiHandler = {
  createUserAction: async (payload: CreateUserActionPayload): Promise<ApiResponse<UserAction>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).post(`/create-user-action`, payload),

  addActionsToUserAction: async (
    payload: AddActionsToUserActionPayload,
  ): Promise<ApiResponse<UserAction[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).post(`/add-actions-to-user-action`, payload),

  updateUserAction: async (
    actionId: number,
    payload: UpdateUserActionPayload,
  ): Promise<ApiResponse<UserAction>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).patch(
      `/update-user-action/${actionId}`,
      payload,
    ),

  removeUserAction: async (actionId: number): Promise<ApiResponse<null>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).delete(`/remove-user-action/${actionId}`),

  getUserActions: async (params?: GetUserActionsParams): Promise<ApiResponse<UserAction[]>> => {
    const queryParams = new URLSearchParams();
    if (params?.completed !== undefined) {
      queryParams.append('completed', params.completed.toString());
    }

    if (params?.started !== undefined) {
      queryParams.append('started', params.started.toString());
    }

    if (params?.actionType) {
      queryParams.append('actionType', params.actionType);
    }

    const queryString = queryParams.toString();
    const url = queryString ? `/get-user-actions?${queryString}` : '/get-user-actions';

    return createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(url);
  },

  getInsightsUserActions: async (insightId: number): Promise<ApiResponse<UserAction[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      `/get-recommended-indicator-actions/${insightId}`,
    ),

  getQuestionnaireUserActions: async (
    questionnaireId: number,
  ): Promise<ApiResponse<UserAction[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      `/get-recommended-questionnaire-actions/${questionnaireId}`,
    ),

  // REFERENCE CAN BE QUESTIONNAIRE ID OR LEARNING ID
  getUserActionsByReference: async (referenceId: number): Promise<ApiResponse<UserAction[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      `/get-user-actions-by-reference/${referenceId}`,
    ),
};

export type ActionType = 'USER_ACTION' | 'LEARNING';

export interface UserAction {
  id: number;
  actionType: ActionType;
  userId: number;
  actionLibraryId: number | null;
  title: string | null;
  description: string | null;
  actionButtonTitle: string | null;
  url: string | null;
  dueDate: string;
  orderId: number;
  started: boolean;
  startedAt: string | null;
  completed: boolean;
  completedAt: string | null;
  removed: boolean;
  removedAt: string | null;
  createdBy: string;
  createdByUserId: number;
  createdAt: string;
  updatedAt: string;
  dueDays: number;
  actionImage: string | null;
}

export interface CreateUserActionPayload {
  userId: number;
  title: string;
  description: string | null;
  actionButtonTitle: string | null;
  url: string | null;
  dueDate: string | null;
  createdBy: string;
}

export interface AddActionsToUserActionPayload {
  questionnaireId: number;
  focusAreas: number[];
  indicators: number[] | null;
}

export interface UpdateUserActionPayload {
  dueDate: string | null;
  started: boolean | null;
  completed: boolean | null;
}

export interface GetUserActionsParams {
  completed?: boolean;
  started?: boolean;
  actionType?: ActionType;
}

export const useUserActionsHooks = {
  // GET /get-user-actions
  useUserActions: (params?: GetUserActionsParams) =>
    useQuery({
      queryKey: ['user-actions', params],
      queryFn: () => userActionsApiHandler.getUserActions(params),
      // staleTime: 2 * 60 * 1000, // 2 minutes
    }),

  // POST /create-user-action
  useCreateUserAction: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: (payload: CreateUserActionPayload) =>
        userActionsApiHandler.createUserAction(payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['user-actions'] });
      },
      onError: (error) => {
        console.error('Failed to create user action:', error);
      },
    });
  },

  // POST /add-actions-to-user-action
  useAddActionsToUserAction: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: (payload: AddActionsToUserActionPayload) =>
        userActionsApiHandler.addActionsToUserAction(payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['user-actions'] });
      },
      onError: (error) => {
        console.error('Failed to add actions to user action:', error);
      },
    });
  },

  // PATCH /update-user-action/{actionId}
  useUpdateUserAction: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: ({ actionId, payload }: { actionId: number; payload: UpdateUserActionPayload }) =>
        userActionsApiHandler.updateUserAction(actionId, payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['user-actions'] });
      },
      onError: (error) => {
        console.error('Failed to update user action:', error);
      },
    });
  },

  // DELETE /remove-user-action/{actionId}
  useRemoveUserAction: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: (actionId: number) => userActionsApiHandler.removeUserAction(actionId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['user-actions'] });
      },
      onError: (error) => {
        console.error('Failed to remove user action:', error);
      },
    });
  },

  useGetQuestionnaireActions: (questionnaireId: number | null) =>
    useQuery({
      queryKey: ['questionnaire-actions', questionnaireId],
      queryFn: () => userActionsApiHandler.getQuestionnaireUserActions(questionnaireId || 0),
      enabled: !!questionnaireId && questionnaireId !== 0,
    }),

  useGetInsightActions: (insightId: number | null) =>
    useQuery({
      queryKey: ['insight-actions', insightId],
      queryFn: () => userActionsApiHandler.getInsightsUserActions(insightId || 0),
      enabled: !!insightId && insightId !== 0,
    }),

  useGetUserActionsByReference: (referenceId: number | null) =>
    useQuery({
      queryKey: ['user-actions-by-reference', referenceId],
      queryFn: () => userActionsApiHandler.getUserActionsByReference(referenceId || 0),
      enabled: !!referenceId && referenceId !== 0,
    }),
};
